import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  View,
  StyleSheet,
  TouchableOpacity,
  Image,
  FlatList,
  ActivityIndicator,
} from "react-native";
import type { FC } from "react";
import { getLedgerItemModalData } from "@services/api";
import t from "@constants/localization";
import ProfileSmall from "@assets/svg/LedgerTxns/ProfileSmall";
import color from "@constants/color";
import Text from "@components/CustomText";

const PATH = "LedgerTxns/ItemDetailsModal";

interface ItemDetailsModalProps {
  itemDetailsModalVisible: boolean;
  setItemDetailsModalVisible: (visible: boolean) => void;
  itemDetailsModalData: {
    modal_api?: string;
  };
}

interface LedgerItem {
  product_image: string;
  short_name: string;
  amount_txt: string;
  quantity: number;
  user_name: string;
  timestamp_txt: string;
  title?: string;
  is_paid_online: boolean;
}

interface ItemDetails {
  items: LedgerItem[];
  amount_txt?: string;
  quantity?: number;
  item_count?: number;
  type?: "ITEM" | "CRATE" | "CUSTOMER";
}

const ItemDetailsModal: FC<ItemDetailsModalProps> = ({
  itemDetailsModalVisible,
  setItemDetailsModalVisible,
  itemDetailsModalData,
}) => {
  const [itemDetails, setItemDetails] = useState<ItemDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (itemDetailsModalVisible && itemDetailsModalData?.modal_api) {
      getItemDetails();
    } else {
      setItemDetails(null);
    }
  }, [itemDetailsModalData, itemDetailsModalVisible]);

  const getItemDetails = async () => {
    try {
      setIsLoading(true);
      const modalApi = itemDetailsModalData?.modal_api;
      const response = await getLedgerItemModalData({ modal_api: modalApi });
      if (mountedRef.current) {
        setItemDetails(response.data || null);
      }
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
      }
    }
  };

  const renderHeader = () => (
    <View style={styles.header}>
      <Text variant="title_18_semi" style={styles.headerTitle}>
        {t(
          `LedgerTxns/${itemDetails?.type === "ITEM" ? "ItemDetailsModal" : itemDetails?.type === "CRATE" ? "CrateDetailsModal" : "CustomerDetailsModal"}/title`,
        )}
      </Text>
      <TouchableOpacity
        onPress={() => setItemDetailsModalVisible(false)}
        style={styles.closeButton}
      >
        <Text style={styles.closeButtonText}>✕</Text>
      </TouchableOpacity>
    </View>
  );

  const renderContent = () => {
    if (isLoading) {
      return (
        <View style={styles.loaderContainer}>
          <ActivityIndicator size="large" color={color.primary} />
        </View>
      );
    }

    return (
      <>
        {renderSummary()}
        {renderItems()}
      </>
    );
  };

  const renderSummary = () => (
    <View style={styles.summaryContainer}>
      {itemDetails?.amount_txt ? (
        <View style={styles.summaryItem}>
          <Text style={styles.summaryLabel}>{t(`${PATH}/amount`)}:</Text>
          <Text variant="title_18_semi" style={styles.summaryValue}>
            {itemDetails.amount_txt}
          </Text>
        </View>
      ) : null}
      {itemDetails?.quantity ? (
        <View style={styles.summaryItem}>
          <Text style={styles.summaryLabel}>{t(`${PATH}/quantity`)}:</Text>
          <Text variant="title_18_semi" style={styles.summaryValue}>
            {itemDetails.quantity}
          </Text>
        </View>
      ) : null}
      {itemDetails?.item_count ? (
        <View style={styles.summaryItem}>
          <Text style={styles.summaryLabel}>{t(`${PATH}/item`)}:</Text>
          <Text variant="title_18_semi" style={styles.summaryValue}>
            {itemDetails.item_count}
          </Text>
        </View>
      ) : null}
    </View>
  );

  const renderItem = ({ item }: { item: LedgerItem }) => {
    if (itemDetails?.type === "ITEM") {
      return (
        <View style={styles.itemContainer}>
          <View style={styles.productRow}>
            <Image
              source={{ uri: item.product_image }}
              style={styles.productImage}
              resizeMode="contain"
            />
            <View style={styles.productDetails}>
              <Text variant="body_text_4" style={styles.productName}>
                {item.short_name || ""}
              </Text>
              <View style={styles.priceRow}>
                <Text variant="body_text_2" style={styles.price}>
                  {item.amount_txt || ""}
                </Text>
                <Text
                  variant="body_text_3"
                  style={styles.quantity}
                >{`Qty: ${item.quantity || ""}`}</Text>
              </View>
            </View>
          </View>
          <View style={styles.userInfo}>
            <View style={styles.userNameContainer}>
              <ProfileSmall />
              <Text variant="caption_1" style={styles.userName}>
                {item.user_name}
              </Text>
            </View>
            <Text variant="caption_2" style={styles.penaltyDate}>
              {item.timestamp_txt}
            </Text>
          </View>
        </View>
      );
    }

    if (itemDetails?.type === "CRATE") {
      return (
        <View style={[styles.itemContainer, styles.crateItemRow]}>
          <Text variant="body_text_1" style={styles.productName}>
            {item.title}
          </Text>
          <Text variant="caption_2" style={styles.penaltyDate}>
            {item.timestamp_txt}
          </Text>
        </View>
      );
    }

    return (
      <View style={[styles.itemContainer]}>
        <View style={styles.customerRow}>
          <View>
            <Text variant="body_text_3" style={styles.customerName}>
              {item.title}
            </Text>
            <Text variant="caption_3" style={styles.customerDate}>
              {item.timestamp_txt}
            </Text>
          </View>
          {item.is_paid_online ? (
            <View style={styles.paidContainer}>
              <Text style={styles.paidText}>
                {t(`LedgerTxns/CustomerDetailsModal/paidOnline`)}
              </Text>
            </View>
          ) : (
            <Text variant="title_16" style={styles.customerPrice}>
              {item.amount_txt}
            </Text>
          )}
        </View>
      </View>
    );
  };

  const renderItems = () => (
    <FlatList
      data={itemDetails?.items}
      renderItem={renderItem}
      keyExtractor={(_, index) => index.toString()}
      showsVerticalScrollIndicator={false}
      style={styles.itemList}
    />
  );

  return (
    <Modal
      animationType="fade"
      transparent
      visible={itemDetailsModalVisible}
      onRequestClose={() => setItemDetailsModalVisible(false)}
    >
      <View style={styles.centeredView}>
        <TouchableOpacity
          style={styles.modalOverlay}
          onPress={() => setItemDetailsModalVisible(false)}
        />
        <View style={styles.modalView}>
          {renderHeader()}
          {renderContent()}
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  centeredView: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
  },
  modalOverlay: {
    flex: 1,
    justifyContent: "flex-end",
  },
  modalView: {
    backgroundColor: color.white,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    minHeight: "70%",
    maxHeight: "75%",
  },
  loaderContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 40,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 10,
    paddingHorizontal: 16,
  },
  headerTitle: {
    color: color.mediumBlack,
  },
  closeButton: {
    padding: 8,
  },
  closeButtonText: {
    fontSize: 20,
    color: "#666",
  },
  summaryContainer: {
    flexDirection: "row",
    paddingHorizontal: 16,
    paddingBottom: 12,
  },
  summaryItem: {
    flexDirection: "row",
    paddingHorizontal: 8,
    backgroundColor: "#F8F8F8",
    marginRight: 8,
    borderRadius: 8,
    justifyContent: "center",
    alignItems: "center",
  },
  summaryLabel: {
    fontSize: 16,
    color: "#000",
    marginHorizontal: 8,
    fontWeight: "400",
  },
  summaryValue: {
    color: color.mediumBlack,
    lineHeight: 38,
  },
  itemList: {
    backgroundColor: color.white,
    paddingHorizontal: 16,
    paddingBottom: 0,
  },
  itemContainer: {
    backgroundColor: "rgba(248, 248, 248, 1)",
    borderRadius: 12,
    padding: 12,
    marginBottom: 12,
  },
  crateItemRow: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  productRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
  },
  productImage: {
    width: 60,
    height: 60,
    borderRadius: 8,
    marginRight: 12,
  },
  productDetails: {
    flex: 1,
  },
  productName: {
    fontSize: 16,
    fontWeight: "600",
    color: color.mediumBlack,
    marginBottom: 4,
  },
  priceRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  price: {
    fontSize: 16,
    fontWeight: "700",
    color: color.mediumBlack,
    marginRight: 8,
  },
  customerPrice: {
    fontSize: 16,
    fontWeight: "700",
    color: color.primary,
  },
  quantity: {
    color: "#636e72",
  },
  userInfo: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderTopColor: color.shadowLighter,
    borderTopWidth: 1,
    paddingTop: 6,
    marginTop: 8,
  },
  userNameContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  userName: {
    color: color.mediumBlack,
    marginLeft: 4,
  },
  penaltyDate: {
    color: color.mediumBlacklightBlack,
    lineHeight: 21,
  },
  customerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    marginBottom: 4,
  },
  paidContainer: {
    backgroundColor: "rgba(233, 255, 206, 1)",
    borderRadius: 8,
    padding: 4,
    paddingHorizontal: 8,
  },
  paidText: {
    fontSize: 11,
    fontWeight: "700",
    color: color.btnGreen,
  },
  customerName: {
    color: color.mediumBlack,
  },
  customerDate: {
    color: color.mediumBlacklightBlack,
  },
});

export default ItemDetailsModal;
